/**
 * SOLID Review
 * S — Good. One concern: the account — signing in by code, signing up in place, the profile's two edits,
 *     and signing out. Each is a rule one auth screen applied before or after its server call.
 * O — Good. A new auth step is one plan/answer pair here; the screens show its message unchanged.
 * L — N/A — no inheritance hierarchy.
 * I — Good. One function per op on the one ClientDecisions contract.
 * D — Strong. The Domain's own value object, the session draft and the port schemas.
 */
import { decided, refuse, REFUSAL, type IEntityDecision, type TDecisionResult } from '../shared';
import { draftOf, settleSession, type IConfigSettleSession } from './sessionDraft';
import { EMPTY_TABLE } from '../table/tableDraft';
import { EmailAddress } from '../../auth/EmailAddress';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityClientAccount } from '../../ports/schemas/ClientSession';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type { ICommandNothing } from '../../ports/schemas/TableMoves';
import type {
  ICommandEmail, ICommandSignUp, ICommandVerifyCode, ICommandNickname, ICommandEmailCode, ICommandLoginCodeAnswered,
  ICommandSignedUp, ICommandSignedIn, ICommandProfileLoaded, ICommandEmailChanged, IPayloadEmail, IPayloadSignUp,
  IPayloadVerifyCode, IPayloadNickname, IPayloadEmailCode, IEntityLoginCode,
} from '../../ports/schemas/AuthMoves';

/**
 * THE ACCOUNT (frontend strict-ports plan, FE3, 2026-10-03). LoginScreen, VerifyCodeScreen, ProfileModal and
 * the welcome screen's logout each checked their fields and wrote the auth store around one REST call. The
 * checks and the writes are here now, moved and not rewritten; the messages are the screens', word for word.
 * What the screens keep is their fields, their busy flags and the message they show.
 *
 * The token is never here: a code accepted is an identity, and `ClientAuthServer` keeps the JWT that came
 * with it. Signing out forgets the token there and everything else here.
 */

type TSession<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

const NOTHING: IPropsClientChanges = {};
const APPLIED: IEntityTableAck = { applied: true };
const NOT_APPLIED: IEntityTableAck = { applied: false };
/** ProfileModal's limit on a nickname, the same as the guest name's. */
const MAX_NICKNAME_LENGTH = 32;
const CODE_LENGTH = 4;

const INVALID_EMAIL = 'Please enter a valid email address.';
const NO_NICKNAME = 'Please enter a nickname.';
const NO_CONSENT = 'You must accept the Terms of Service and Privacy Policy to continue.';
/** The login code's refusal when the server gave no words of its own. */
const LOGIN_CODE_FAILED = 'Failed to send code. Please try again.';
/** The server's answer to a login code for an address it does not know (LoginScreen's test). */
const NO_ACCOUNT = /no account/i;

function normalised(email: string): string {
  return email.trim().toLowerCase();
}

function signedInOnly(input: IPropsClientInput): boolean {
  return input.session.account.kind === 'registered';
}

/** The email screen's Continue in sign-in mode: an address, normalised. */
export function decidePlanLoginCode(_input: IPropsClientInput, command: ICommandEmail): TSession<IPayloadEmail> {
  if (!EmailAddress.isValid(command.email)) return refuse(INVALID_EMAIL, REFUSAL.INVALID_INPUT);
  const request: IPayloadEmail = { email: normalised(command.email) };
  return decided(NOTHING, request);
}

/**
 * The login code's answer. Sent: on to the code screen with the address. "No account": the email screen
 * becomes the sign-up form in place, no error shown. Any other refusal: the server's words.
 */
export function decideLoginCodeAnswered(input: IPropsClientInput, command: ICommandLoginCodeAnswered): TSession<IEntityLoginCode> {
  const { email, refusal } = command;
  if (refusal !== null) {
    if (NO_ACCOUNT.test(refusal)) {
      const signUp: IEntityLoginCode = { signUp: true };
      return decided(NOTHING, signUp);
    }
    return refuse(refusal || LOGIN_CODE_FAILED, REFUSAL.SERVER);
  }
  const next = draftOf(input);
  next.session.pendingEmail = email;
  next.session.screen = 'VERIFY_LOGIN';
  const sent: IEntityLoginCode = { signUp: false };
  return decided(settleSession(input, next), sent);
}

/** The sign-up form's Continue: an address, a nickname and the consent the law requires. */
export function decidePlanSignUp(_input: IPropsClientInput, command: ICommandSignUp): TSession<IPayloadSignUp> {
  if (!EmailAddress.isValid(command.email)) return refuse(INVALID_EMAIL, REFUSAL.INVALID_INPUT);
  const displayName = command.displayName.trim();
  if (displayName.length === 0) return refuse(NO_NICKNAME, REFUSAL.INVALID_NAME);
  if (!command.consentAccepted) return refuse(NO_CONSENT, REFUSAL.INVALID_INPUT);
  const request: IPayloadSignUp = {
    email: command.email.trim(),
    displayName,
    referralCode: command.referralCode,
    consentAccepted: true,
  };
  return decided(NOTHING, request);
}

/** Signed up: on to the code screen with the address, to verify it. */
export function decideSignedUp(input: IPropsClientInput, command: ICommandSignedUp): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.session.pendingEmail = normalised(command.email);
  next.session.screen = 'VERIFY_EMAIL';
  return decided(settleSession(input, next), APPLIED);
}

/** The code screen's Verify: four digits, for the address the code was sent to. */
export function decidePlanVerifyCode(input: IPropsClientInput, command: ICommandVerifyCode): TSession<IPayloadVerifyCode> {
  const email = input.session.pendingEmail;
  if (email === null) return refuse('No address is waiting for a code.', REFUSAL.NOT_READY);
  if (command.code.length !== CODE_LENGTH) return refuse('Please enter all 4 digits.', REFUSAL.INVALID_INPUT);
  const request: IPayloadVerifyCode = { purpose: command.purpose, email, code: command.code };
  return decided(NOTHING, request);
}

/**
 * A code accepted (the auth store's `setAuth`): the tab is that registered account, on the game screen —
 * where the entry asks `me` and seats them — and a guest identity it held is forgotten.
 */
export function decideSignedIn(input: IPropsClientInput, command: ICommandSignedIn): TSession<IEntityTableAck> {
  const { identity } = command;
  const next = draftOf(input);
  const account: IEntityClientAccount = {
    kind: 'registered', playerId: identity.playerId, displayName: identity.displayName, email: null, matchCredits: identity.matchCredits,
  };
  next.session.account = account;
  next.session.screen = 'GAME';
  const forgetGuest: IConfigSettleSession = { guest: null };
  return decided(settleSession(input, next, forgetGuest), APPLIED);
}

/** The profile opened and `me` answered: the account's name, address and credits as the server has them. */
export function decideProfileLoaded(input: IPropsClientInput, command: ICommandProfileLoaded): TSession<IEntityTableAck> {
  if (!signedInOnly(input)) return decided(NOTHING, NOT_APPLIED);
  const { profile } = command;
  const next = draftOf(input);
  next.session.account = { ...input.session.account, displayName: profile.displayName, email: profile.email, matchCredits: profile.matchCredits };
  return decided(settleSession(input, next), APPLIED);
}

/** The profile's Save: a nickname, trimmed, one to 32 characters. */
export function decidePlanNickname(input: IPropsClientInput, command: ICommandNickname): TSession<IPayloadNickname> {
  if (!signedInOnly(input)) return refuse('Only a signed-in player has a profile.', REFUSAL.NOT_READY);
  const displayName = command.displayName.trim();
  if (displayName.length === 0) return refuse(NO_NICKNAME, REFUSAL.INVALID_NAME);
  if (displayName.length > MAX_NICKNAME_LENGTH) return refuse(`Name must be ${MAX_NICKNAME_LENGTH} characters or fewer.`, REFUSAL.INVALID_NAME);
  const request: IPayloadNickname = { displayName };
  return decided(NOTHING, request);
}

/** The new nickname: the account's, and the seated player's — the table shows the name it holds. */
export function decideNicknameChanged(input: IPropsClientInput, command: ICommandNickname): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.session.account = { ...input.session.account, displayName: command.displayName };
  if (input.session.player !== null) next.session.player = { ...input.session.player, displayName: command.displayName };
  return decided(settleSession(input, next), APPLIED);
}

/** The profile's "send code" for a new address: an address, normalised. */
export function decidePlanEmailChange(input: IPropsClientInput, command: ICommandEmail): TSession<IPayloadEmail> {
  if (!signedInOnly(input)) return refuse('Only a signed-in player has a profile.', REFUSAL.NOT_READY);
  const email = normalised(command.email);
  if (!EmailAddress.isValid(email)) return refuse(INVALID_EMAIL, REFUSAL.INVALID_INPUT);
  const request: IPayloadEmail = { email };
  return decided(NOTHING, request);
}

/** The profile's Verify for a new address: four digits. */
export function decidePlanVerifyEmailChange(input: IPropsClientInput, command: ICommandEmailCode): TSession<IPayloadEmailCode> {
  if (!signedInOnly(input)) return refuse('Only a signed-in player has a profile.', REFUSAL.NOT_READY);
  const code = command.code.trim();
  if (code.length !== CODE_LENGTH) return refuse('Enter the 4-digit code.', REFUSAL.INVALID_INPUT);
  const request: IPayloadEmailCode = { email: normalised(command.email), code };
  return decided(NOTHING, request);
}

/** The new address, as the server confirmed it. */
export function decideEmailChanged(input: IPropsClientInput, command: ICommandEmailChanged): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.session.account = { ...input.session.account, email: command.email };
  return decided(settleSession(input, next), APPLIED);
}

/**
 * Signing out — the auth store's `clearAuth` and `clearGuest`, one decision for both: the table is reset
 * (the composition root's reset handler), nobody is signed in, the welcome screen. The guest identity is
 * forgotten, and — a FIX, §28.199's finding — so is the stored game: the next player in this tab must not
 * find another player's record, which the entry would trust without asking. A pending join and its error
 * are left as they were, so the expiry message a refused token leaves stays on the welcome screen.
 */
export function decideSignedOut(input: IPropsClientInput, _command: ICommandNothing): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.table = { ...EMPTY_TABLE };
  const nobody: IEntityClientAccount = { kind: 'anonymous', playerId: null, displayName: null, email: null, matchCredits: 0 };
  next.session.account = nobody;
  next.session.player = null;
  next.session.screen = 'WELCOME';
  next.session.pendingEmail = null;
  next.session.hostOnEntry = false;
  // The reset clears these with the rest of the game store; the draft says so, so the changes agree.
  next.session.restoring = false;
  next.session.entering = false;
  next.session.hosting = false;
  next.session.hostError = null;
  const forget: IConfigSettleSession = { reset: true, guest: null, resume: null };
  return decided(settleSession(input, next, forget), APPLIED);
}
